import { Link, useLocation } from '@tanstack/react-router';
import { Rocket, ArrowRight } from 'lucide-react';
import { useOnboardingStatus } from '@/hooks/onboarding/useOnboardingStatus';
import { useAuthPermissions } from '@/stores/auth.store';

/**
 * Slim, full-width reminder shown across the dashboard while tenant onboarding
 * is unfinished — the "come back to setup" affordance for admins who peeked at
 * the dashboard before completing the checklist.
 *
 * Renders nothing when: onboarding is complete, the user can't do setup
 * (no `company:edit` — e.g. a technician), or we're already on the onboarding
 * page. It disappears on its own the moment the last task is done.
 */
export function OnboardingReminderBanner() {
  const permissions = useAuthPermissions();
  const canSetUp = permissions.includes('company:edit');
  const { pathname } = useLocation();
  const onOnboardingPage = pathname.startsWith('/dashboard/onboarding');

  // Only fetch for setup-capable users off the onboarding page — the banner
  // can never render otherwise, so don't hit the endpoint on every shell mount.
  const { data } = useOnboardingStatus(canSetUp && !onOnboardingPage);

  if (!canSetUp || onOnboardingPage) return null;
  if (!data || data.complete) return null;

  const remaining = data.totalCount - data.completedCount;

  return (
    <Link
      to="/dashboard/onboarding"
      className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
    >
      <Rocket size={15} className="shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">Finish setting up your account</span>
        <span className="text-amber-800/70 dark:text-amber-300/70">
          {' '}
          — {remaining} step{remaining === 1 ? '' : 's'} left to go live.
        </span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 font-medium">
        Continue setup
        <ArrowRight size={14} />
      </span>
    </Link>
  );
}
